import { useEffect, useMemo, useRef } from "react";
import { useTranslations } from "@zapier/i18n";
import { createPortal } from "react-dom";
import { Icon, IconName } from "@zapier/zinnia-icons";
import {
  Button,
  Heading,
  HeadingLevel,
  IconButton,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
  VisuallyHidden,
} from "@zapier/design-system";
import styles from "./BulkActionsBar.module.css";
import { useBulkActionsBarScrolling } from "./useBulkActionsBarScrolling";
import { BulkSelectAction } from "./BulkActions";

interface Props {
  assetTypeLabel: string;
  selectedRows: string[];
  clearSelectedEntities: () => void;
  bulkSelectActions?: BulkSelectAction[];
  emptyStateMessage?: string;
}

export function BulkActionsBar({
  assetTypeLabel,
  selectedRows,
  clearSelectedEntities,
  bulkSelectActions = [],
  emptyStateMessage,
}: Props) {
  const t = useTranslations("bulk-actions-bar");

  const selectedCount = selectedRows.length;
  const { isFixed, ref, target } = useBulkActionsBarScrolling();

  const visibleActions = useMemo(
    () =>
      bulkSelectActions.filter(
        (action) => !action.isVisible || action.isVisible(selectedRows),
      ),
    [bulkSelectActions, selectedRows],
  );

  const notifiedReference = useRef<{
    selectedRows: string[];
    actionKeys: Set<string>;
  }>();

  // Notifies on selection change and when an action first becomes visible, but
  // not when the action objects are merely recreated.
  useEffect(() => {
    const previous = notifiedReference.current;
    const hasSelectionChanged = previous?.selectedRows !== selectedRows;
    const actionKeys = new Set<string>();

    for (const action of visibleActions) {
      if (!action.onSelectionChange) continue;
      const actionKey = action.id ?? action.text;
      actionKeys.add(actionKey);
      if (hasSelectionChanged || !previous?.actionKeys.has(actionKey)) {
        action.onSelectionChange(selectedRows);
      }
    }

    notifiedReference.current = { selectedRows, actionKeys };
  }, [visibleActions, selectedRows]);

  return createPortal(
    <div className={`${styles.root} ${isFixed && styles.fixedBar}`} ref={ref}>
      {/* role="toolbar" + aria-label give the group of bulk-action controls a
          programmatic name so assistive tech can identify it (WCAG ENT-540). */}
      <div role="toolbar" aria-label={t("heading.bulk-actions")}>
        {/* A heading makes it easier for screen reader users to navigate to the bulk actions bar from the table. */}
        <VisuallyHidden>
          <HeadingLevel>
            <Heading>{t("heading.bulk-actions")}</Heading>
          </HeadingLevel>
        </VisuallyHidden>

        {!!selectedCount && (
          <div className={styles.selectedCount}>
            <Text type="Body1" color="PrimeWhite">
              {t("selection.count-selected", { selectedCount: selectedCount })}
            </Text>
          </div>
        )}

        <div className={styles.buttonsWrapper}>
          {visibleActions.length === 0 ? (
            emptyStateMessage ? (
              <Text type="Body1" color="PrimeWhite">
                {emptyStateMessage}
              </Text>
            ) : undefined
          ) : (
            visibleActions.map((action, index) => {
              const dynamicState = action.getDisabledState?.(selectedRows);
              const item = {
                ...action,
                disabled: action.disabled || dynamicState?.disabled,
                tooltipText: dynamicState?.tooltipText ?? action.tooltipText,
              };
              const button = (
                <Button
                  key={index}
                  iconPosition="start"
                  onPress={() => item.onClick(selectedRows)}
                  size="medium"
                  isDisabled={item.disabled}
                  aria-label={t("actions.action-with-count", {
                    actionText: item.text,
                    selectedCount,
                  })}
                >
                  <Icon name={item.icon as IconName} size={30} />
                  <span className={styles.buttonText}>{item.text}</span>
                </Button>
              );

              if (item.disabled && item.tooltipText) {
                return (
                  <Tooltip
                    key={index}
                    allowMultiline={true}
                    content={item.tooltipText}
                    placement="top"
                  >
                    <TooltipFocusableTrigger>{button}</TooltipFocusableTrigger>
                  </Tooltip>
                );
              }

              return button;
            })
          )}
        </div>

        <div className={styles.closeButton}>
          <Tooltip
            allowMultiline={true}
            content={t("actions.deselect")}
            key="deselect-all"
            placement="top"
          >
            <TooltipFocusableTrigger>
              <IconButton
                aria-label={t("actions.deselect-all-items", {
                  assetTypeLabel,
                })}
                name="formX"
                variant="primary"
                onPress={clearSelectedEntities}
                size="small"
              />
            </TooltipFocusableTrigger>
          </Tooltip>
        </div>
      </div>
    </div>,
    target,
  );
}
